import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetLaborExtra, updateProps } from '../../contextRedux/LaborExtra/LaborExtraModel';
import { setLaborExtras } from '../../contextRedux/LaborExtra/LaborExtraStore';
import LaborExtraService from '../../services/laborExtra/LaborExtraService';
import { RootState } from '../../store';
import LaborExtraModal from '../LaborExtra/LaborExtraModal/LaborExtraModal';
import LaborExtraTable from '../LaborExtra/LaborExtraTable/LaborExtraTable';
import { AnimateButton } from '../../customComponents/button/CustomButton';

const LaborExtra: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.laborExtra.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.laborExtra.props.skip);
  const take = useSelector((state: RootState) => state.models?.laborExtra.props.take);
  const refresh = useSelector((state: RootState) => state.models?.laborExtra.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isLaborExtrasLoading,
    isError: isErrorOnFetchLaborExtras,
    error: laborExtrasError,
    refetch,
  } = useQuery({
    queryKey: ['laborExtras', searchTerm, skip, take, refresh],
    queryFn: () =>
      LaborExtraService.search({
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: ControllerResponse<any>) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setLaborExtras(data?.response));
        return data?.response;
      }),
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetLaborExtra({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isLaborExtrasLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchLaborExtras) return <Alert message={laborExtrasError.message} type='error' />;

  return (
    <>
      <h1 style={{marginBottom:50}}>Manopera Extra</h1>
      <AnimateButton onClick={() => handleReset()}>
        Adauga
      </AnimateButton>
      <LaborExtraModal />
      <LaborExtraTable />
    </>
  );
};

export default LaborExtra;
